import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { User } from '../types';
import api from '../utils/api';

interface ProfileSettingScreenProps {
    currentUser: User;
    onBack: () => void;
    onUpdate: (updatedUser: User) => void;
    onNavigateToDetailSettings?: () => void;
    onSaveRequest?: () => void;
    showSaveButton?: boolean;
}

export interface ProfileSettingScreenRef {
    handleSave: () => Promise<void>;
}

const ProfileSettingScreen = forwardRef<ProfileSettingScreenRef, ProfileSettingScreenProps>(
    (
        {
            currentUser,
            onBack,
            onUpdate,
            onNavigateToDetailSettings,
            onSaveRequest,
            showSaveButton = true,
        },
        ref
    ) => {
        const [username, setUsername] = useState(currentUser.username || '');
        const [profileImage, setProfileImage] = useState<string | undefined>(undefined);
        const [gender, setGender] = useState<string>('');
        const [hobbies, setHobbies] = useState<string>('');
        const [bio, setBio] = useState<string>('');
        const [saving, setSaving] = useState(false);
        const [loading, setLoading] = useState(true);
        const [uploading, setUploading] = useState(false);
        const [isMobile, setIsMobile] = useState(false);
        const [showDetailSettings, setShowDetailSettings] = useState(false);
        const [callAcceptance, setCallAcceptance] = useState<
            'everyone' | 'favorites_only' | 'none'
        >('everyone');
        const [showCallAcceptanceDropdown, setShowCallAcceptanceDropdown] = useState(false);
        const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
        const [deletePassword, setDeletePassword] = useState('');
        const [deleteError, setDeleteError] = useState('');
        const [deleteSuccess, setDeleteSuccess] = useState('');
        const [isDeletingAccount, setIsDeletingAccount] = useState(false);

        const API_URL = 'https://voicechatapi.kasaneko.com';

        useEffect(() => {
            const checkMobile = () => {
                setIsMobile(window.innerWidth <= 768);
            };
            checkMobile();
            window.addEventListener('resize', checkMobile);
            return () => window.removeEventListener('resize', checkMobile);
        }, []);

        // ユーザー情報を取得
        useEffect(() => {
            const fetchUserProfile = async () => {
                try {
                    setLoading(true);
                    const response = await fetch(`${API_URL}/api/users/${currentUser.userId}`);
                    const data = await response.json();

                    if (data.success && data.user) {
                        setUsername(data.user.username || '');
                        setProfileImage(data.user.profileImage || undefined);
                        setGender(data.user.gender || '');
                        setHobbies(data.user.hobbies || '');
                        setBio(data.user.bio || '');
                    }
                } catch (error) {
                    console.error('プロフィール取得エラー:', error);
                } finally {
                    setLoading(false);
                }
            };
            fetchUserProfile();

            // 詳細設定も読み込む
            const loadSettings = async () => {
                try {
                    const response = await fetch(
                        `${API_URL}/api/users/settings/${currentUser.userId}`
                    );
                    const data = await response.json();
                    if (data.success && data.settings) {
                        setCallAcceptance(data.settings.callAcceptance || 'everyone');
                    }
                } catch (error) {
                    console.error('設定取得エラー:', error);
                    setCallAcceptance('everyone');
                }
            };
            loadSettings();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [currentUser.userId]);

        // プロフィール画像変更（ファイルアップロード）
        const handleChangeProfileImage = () => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/jpeg,image/png,image/gif,image/webp';

            input.onchange = async (e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (!file) {
                    return;
                }

                // ファイルサイズチェック（5MB）
                if (file.size > 5 * 1024 * 1024) {
                    alert('画像サイズは5MB以下にしてください');
                    return;
                }

                try {
                    setUploading(true);

                    // 画像をBase64に変換
                    const reader = new FileReader();
                    reader.onload = async (event) => {
                        const base64Data = event.target?.result as string;

                        // サーバーにアップロード（古い画像URLも送信）
                        const response = await fetch(`${API_URL}/api/upload/profile-image-base64`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                imageBase64: base64Data,
                                oldImageUrl: profileImage, // 古い画像URLを送信
                            }),
                        });

                        const data = await response.json();

                        if (data.success) {
                            // S3のURLは完全なURLなので、そのまま使用
                            setProfileImage(data.imageUrl);
                            alert('✅ 画像をアップロードしました！');
                        } else {
                            alert(data.error || '画像アップロードに失敗しました');
                        }

                        setUploading(false);
                    };

                    reader.onerror = () => {
                        alert('画像の読み込みに失敗しました');
                        setUploading(false);
                    };

                    reader.readAsDataURL(file);
                } catch (error) {
                    console.error('画像アップロードエラー:', error);
                    alert('画像アップロード中にエラーが発生しました');
                    setUploading(false);
                }
            };

            input.click();
        };

        // プロフィール保存（プロフィール情報と詳細設定の両方を保存）
        const handleSave = async () => {
            try {
                setSaving(true);

                // プロフィール情報を保存
                const profileResponse = await fetch(`${API_URL}/api/users/profile`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userId: currentUser.userId,
                        username,
                        profileImage,
                        gender,
                        hobbies: hobbies.slice(0, 100),
                        bio: bio.slice(0, 100),
                        oldProfileImage: currentUser.profileImage, // 古い画像URLを送信
                    }),
                });

                const profileData = await profileResponse.json();

                if (!profileData.success) {
                    alert(profileData.error || 'プロフィール更新に失敗しました');
                    return;
                }

                // 詳細設定（通話承諾）も保存
                const settingsResponse = await fetch(
                    `${API_URL}/api/users/settings/${currentUser.userId}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ callAcceptance }),
                    }
                );

                const settingsData = await settingsResponse.json();

                if (!settingsData.success) {
                    alert(settingsData.error || '設定の保存に失敗しました');
                    return;
                }

                // ユーザー情報を更新（画面遷移はしない）
                const updatedUser = {
                    ...currentUser,
                    username,
                    profileImage,
                    gender,
                    hobbies,
                    bio,
                };

                localStorage.setItem('user', JSON.stringify(updatedUser));
                onUpdate(updatedUser);

                alert('✅ プロフィールと設定を保存しました！');
            } catch (error) {
                console.error('保存エラー:', error);
                alert('保存中にエラーが発生しました');
            } finally {
                setSaving(false);
            }
        };

        // refを通じて外部からhandleSaveを呼び出せるようにする
        useImperativeHandle(ref, () => ({
            handleSave,
        }));

        const handleAccountDeletion = async () => {
            if (!deletePassword || deletePassword.trim().length === 0) {
                setDeleteError('現在のパスワードを入力してください');
                setDeleteSuccess('');
                return;
            }

            setIsDeletingAccount(true);
            setDeleteError('');
            setDeleteSuccess('');

            try {
                const response = await api.delete(`/api/users/${currentUser.userId}`, {
                    data: { currentPassword: deletePassword },
                });

                const message = response.data?.message || 'アカウントを削除しました';
                setDeleteSuccess(message);

                setTimeout(() => {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    window.location.href = '/';
                }, 1500);
            } catch (error: any) {
                const message = error.response?.data?.error || 'アカウントの削除に失敗しました';
                setDeleteError(message);
            } finally {
                setIsDeletingAccount(false);
            }
        };

        if (loading) {
            return (
                <div
                    style={{
                        minHeight: '100vh',
                        background:
                            'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 50%, #dcdcdc 100%)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: '#333',
                        fontSize: '18px',
                    }}
                >
                    読み込み中...
                </div>
            );
        }

        return (
            <>
                <style>
                    {`
          @media (max-width: 768px) {
            .profile-container {
              padding: 10px !important;
              min-height: 100vh !important;
            }
            
            .profile-header {
              flex-direction: column !important;
              gap: 15px !important;
              margin-bottom: 20px !important;
            }
            
            .profile-title {
              font-size: 20px !important;
              text-align: center !important;
            }
            
            .profile-back-button {
              padding: 8px 16px !important;
              font-size: 12px !important;
            }
            
            .profile-form {
              max-width: 100% !important;
              padding: 15px !important;
            }
            
            .profile-form-group {
              margin-bottom: 20px !important;
            }
            
            .profile-label {
              font-size: 14px !important;
              margin-bottom: 8px !important;
            }
            
            .profile-input {
              padding: 10px !important;
              font-size: 14px !important;
            }
            
            .profile-textarea {
              padding: 10px !important;
              font-size: 14px !important;
              min-height: 80px !important;
            }
            
            .profile-button {
              padding: 12px 20px !important;
              font-size: 14px !important;
            }
            
            .profile-avatar {
              width: 80px !important;
              height: 80px !important;
              font-size: 32px !important;
            }
          }
          
          @media (max-width: 480px) {
            .profile-container {
              padding: 5px !important;
            }
            
            .profile-header {
              gap: 10px !important;
              margin-bottom: 15px !important;
            }
            
            .profile-title {
              font-size: 18px !important;
            }
            
            .profile-back-button {
              padding: 6px 12px !important;
              font-size: 11px !important;
            }
            
            .profile-form {
              padding: 10px !important;
            }
            
            .profile-form-group {
              margin-bottom: px !important;
            }
            
            .profile-label {
              font-size: 12px !important;
              margin-bottom: 6px !important;
            }
            
            .profile-input {
              padding: 8px !important;
              font-size: 12px !important;
            }
            
            .profile-textarea {
              padding: 8px !important;
              font-size: 12px !important;
              min-height: 60px !important;
            }
            
            .profile-button {
              padding: 10px 15px !important;
              font-size: 12px !important;
            }
            
            .profile-avatar {
              width: 60px !important;
              height: 60px !important;
              font-size: 24px !important;
            }
          }
        `}
                </style>
                <div
                    className='profile-container'
                    style={{
                        minHeight: isMobile ? '100vh' : 'auto',
                        background: isMobile
                            ? 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 50%, #dcdcdc 100%)'
                            : 'transparent',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: isMobile ? '10px' : '0',
                        fontFamily: 'Arial, sans-serif',
                        color: '#333',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                    }}
                >
                    {/* ヘッダー（スマホのみ表示） */}
                    {isMobile && (
                        <div
                            className='profile-header'
                            style={{
                                width: '100%',
                                maxWidth: '500px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '10px',
                            }}
                        >
                            <h1
                                className='profile-title'
                                style={{ margin: 0, fontSize: '16px', color: '#333' }}
                            >
                                プロフィール設定
                            </h1>
                            <button
                                className='profile-back-button'
                                onClick={onBack}
                                style={{
                                    background: '#667eea',
                                    color: 'white',
                                    border: 'none',
                                    padding: '6px 10px',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                }}
                            >
                                ← メニューに戻る
                            </button>
                        </div>
                    )}

                    {/* メインコンテンツ */}
                    <div
                        className='profile-form'
                        style={{
                            width: '100%',
                            maxWidth: '500px',
                            background: 'rgba(255, 255, 255, 0.9)',
                            padding: isMobile ? '15px' : '25px',
                            paddingBottom: isMobile
                                ? 'calc(180px + max(env(safe-area-inset-bottom, 0px), 10px))'
                                : '25px',
                            borderRadius: isMobile ? '15px 15px 0 0' : '15px',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(0, 0, 0, 0.1)',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                            overflowY: 'auto',
                            height: isMobile ? 'calc(100vh - 100px)' : 'calc(100vh - 120px)',
                            overflowX: 'hidden',
                            marginBottom: '0',
                            position: 'relative',
                        }}
                    >
                        {/* プロフィール画像 */}
                        <div
                            style={{
                                textAlign: 'center',
                                marginBottom: isMobile ? '15px' : '20px',
                                position: 'relative',
                            }}
                        >
                            {/* ユーザーID表示（左上） */}
                            {currentUser.userCode && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '8px',
                                        left: '8px',
                                        background: 'rgba(102, 126, 234, 0.9)',
                                        color: 'white',
                                        padding: isMobile ? '3px 6px' : '4px 8px',
                                        borderRadius: '12px',
                                        fontSize: isMobile ? '9px' : '11px',
                                        fontWeight: 'bold',
                                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                                        zIndex: 10,
                                    }}
                                >
                                    ID: {currentUser.userCode}
                                </div>
                            )}

                            <div
                                style={{
                                    width: isMobile ? '90px' : '90px',
                                    height: isMobile ? '90px' : '90px',
                                    borderRadius: '50%',
                                    background: profileImage
                                        ? `url(${profileImage}) center/cover`
                                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    margin: '0 auto 10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: isMobile ? '36px' : '36px',
                                    boxShadow: '0 3px 12px rgba(0, 0, 0, 0.2)',
                                    border: '2px solid rgba(255, 255, 255, 0.9)',
                                }}
                            >
                                {!profileImage && '👤'}
                            </div>

                            <button
                                onClick={handleChangeProfileImage}
                                disabled={uploading}
                                style={{
                                    background: uploading ? '#ccc' : 'rgba(102, 126, 234, 0.1)',
                                    color: '#333',
                                    border: '1px solid rgba(102, 126, 234, 0.3)',
                                    padding: isMobile ? '6px 12px' : '8px 16px',
                                    borderRadius: '15px',
                                    cursor: uploading ? 'not-allowed' : 'pointer',
                                    fontSize: isMobile ? '11px' : '12px',
                                    fontWeight: 'bold',
                                }}
                            >
                                {uploading ? 'アップロード中...' : '📷 画像を変更'}
                            </button>
                        </div>

                        {/* ユーザーID表示（プロフィール上部） */}
                        {currentUser.userCode && (
                            <div
                                style={{
                                    marginBottom: isMobile ? '12px' : '15px',
                                    textAlign: 'center',
                                }}
                            >
                                <div
                                    style={{
                                        background:
                                            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        border: '2px solid rgba(255, 255, 255, 0.9)',
                                        padding: isMobile ? '8px 12px' : '10px 18px',
                                        borderRadius: '12px',
                                        display: 'inline-block',
                                        boxShadow: '0 3px 10px rgba(102, 126, 234, 0.3)',
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: isMobile ? '9px' : '11px',
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            marginBottom: '5px',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        ユーザーID（変更不可）
                                    </div>
                                    <div
                                        style={{
                                            fontSize: isMobile ? '14px' : '18px',
                                            fontWeight: 'bold',
                                            color: '#ffffff',
                                            letterSpacing: '0.5px',
                                        }}
                                    >
                                        {currentUser.userCode}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ユーザー名（必須） */}
                        <div style={{ marginBottom: isMobile ? '12px' : '15px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: '6px',
                                    fontSize: isMobile ? '11px' : '13px',
                                    fontWeight: 'bold',
                                    color: '#333',
                                }}
                            >
                                👤 ユーザー名 *
                            </label>
                            <input
                                type='text'
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                                style={{
                                    width: '100%',
                                    padding: isMobile ? '8px' : '10px',
                                    border: '1px solid rgba(0, 0, 0, 0.15)',
                                    borderRadius: '8px',
                                    fontSize: isMobile ? '13px' : '14px',
                                    boxSizing: 'border-box',
                                    background: 'rgba(255, 255, 255, 0.9)',
                                    color: '#333',
                                }}
                                placeholder='ユーザー名'
                            />
                        </div>

                        {/* 性別（任意） */}
                        <div style={{ marginBottom: isMobile ? '12px' : '15px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: '6px',
                                    fontSize: isMobile ? '11px' : '13px',
                                    fontWeight: 'bold',
                                    color: '#333',
                                }}
                            >
                                ⚧ 性別（任意）
                            </label>
                            <select
                                value={gender}
                                onChange={(e) => setGender(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: isMobile ? '8px' : '10px',
                                    border: '1px solid rgba(0, 0, 0, 0.15)',
                                    borderRadius: '8px',
                                    fontSize: isMobile ? '13px' : '14px',
                                    boxSizing: 'border-box',
                                    background: 'rgba(255, 255, 255, 0.9)',
                                    color: '#333',
                                }}
                            >
                                <option value=''>選択してください</option>
                                <option value='男性'>男性</option>
                                <option value='女性'>女性</option>
                                <option value='その他'>その他</option>
                                <option value='回答しない'>回答しない</option>
                            </select>
                        </div>

                        {/* 趣味（任意、100文字） */}
                        <div style={{ marginBottom: isMobile ? '12px' : '15px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: '6px',
                                    fontSize: isMobile ? '11px' : '13px',
                                    fontWeight: 'bold',
                                    color: '#333',
                                }}
                            >
                                🎨 趣味（任意）
                            </label>
                            <input
                                type='text'
                                value={hobbies}
                                onChange={(e) => setHobbies(e.target.value.slice(0, 100))}
                                maxLength={100}
                                style={{
                                    width: '100%',
                                    padding: isMobile ? '8px' : '10px',
                                    border: '1px solid rgba(0, 0, 0, 0.15)',
                                    borderRadius: '8px',
                                    fontSize: isMobile ? '13px' : '14px',
                                    boxSizing: 'border-box',
                                    background: 'rgba(255, 255, 255, 0.9)',
                                    color: '#333',
                                }}
                                placeholder='趣味を入力（最大100文字）'
                            />
                            <div
                                style={{
                                    fontSize: isMobile ? '9px' : '11px',
                                    color: '#666',
                                    marginTop: '4px',
                                    textAlign: 'right',
                                }}
                            >
                                {hobbies.length}/100文字
                            </div>
                        </div>

                        {/* 紹介文（任意、100文字） */}
                        <div style={{ marginBottom: isMobile ? '15px' : '20px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    marginBottom: '6px',
                                    fontSize: isMobile ? '11px' : '13px',
                                    fontWeight: 'bold',
                                    color: '#333',
                                }}
                            >
                                ✍️ 紹介文（任意）
                            </label>
                            <textarea
                                value={bio}
                                onChange={(e) => setBio(e.target.value.slice(0, 100))}
                                maxLength={100}
                                rows={isMobile ? 5 : 3}
                                style={{
                                    width: '100%',
                                    padding: isMobile ? '8px' : '10px',
                                    border: '1px solid rgba(0, 0, 0, 0.15)',
                                    borderRadius: '8px',
                                    fontSize: isMobile ? '13px' : '14px',
                                    boxSizing: 'border-box',
                                    background: 'rgba(255, 255, 255, 0.9)',
                                    color: '#333',
                                    resize: 'vertical',
                                    fontFamily: 'Arial, sans-serif',
                                    minHeight: isMobile ? '120px' : 'auto',
                                }}
                                placeholder='自己紹介を入力（最大100文字）'
                            />
                            <div
                                style={{
                                    fontSize: isMobile ? '9px' : '11px',
                                    color: '#666',
                                    marginTop: '4px',
                                    textAlign: 'right',
                                }}
                            >
                                {bio.length}/100文字
                            </div>
                        </div>

                        {/* 詳細設定ボタン */}
                        <button
                            onClick={() => {
                                // スマホもPCも同じ画面内で展開
                                setShowDetailSettings(!showDetailSettings);
                            }}
                            style={{
                                width: '100%',
                                padding: isMobile ? '10px' : '12px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: isMobile ? '12px' : '14px',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                                marginBottom: '15px',
                            }}
                        >
                            ⚙️ 詳細設定 {showDetailSettings ? '▼' : '▶'}
                        </button>

                        {/* 詳細設定展開エリア（スマホ・PC共通） */}
                        {showDetailSettings && (
                            <div
                                style={{
                                    marginBottom: '30px',
                                    padding: '15px',
                                    background: 'rgba(102, 126, 234, 0.05)',
                                    borderRadius: '10px',
                                    border: '1px solid rgba(102, 126, 234, 0.2)',
                                }}
                            >
                                <h3
                                    style={{
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        marginBottom: '12px',
                                        borderBottom: '2px solid rgba(0, 0, 0, 0.1)',
                                        paddingBottom: '8px',
                                        color: '#333',
                                    }}
                                >
                                    通話承諾について
                                </h3>

                                {/* 通話承諾設定：プルダウン形式 */}
                                <div style={{ position: 'relative' }}>
                                    <button
                                        onClick={() =>
                                            setShowCallAcceptanceDropdown(
                                                !showCallAcceptanceDropdown
                                            )
                                        }
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            background: 'rgba(255, 255, 255, 0.9)',
                                            border: '1px solid rgba(102, 126, 234, 0.3)',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: 'bold',
                                            color: '#333',
                                            textAlign: 'left',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <span>
                                            {callAcceptance === 'everyone'
                                                ? '1: 誰からでもOK'
                                                : callAcceptance === 'favorites_only'
                                                ? '2: 自分がお気に入りしている相手からのみ受け取る'
                                                : '3: 通話申請を受け入れない'}
                                        </span>
                                        <span>{showCallAcceptanceDropdown ? '▲' : '▼'}</span>
                                    </button>

                                    {showCallAcceptanceDropdown && (
                                        <div
                                            style={{
                                                width: '100%',
                                                background: 'white',
                                                border: '1px solid rgba(102, 126, 234, 0.3)',
                                                borderRadius: '8px',
                                                marginTop: '5px',
                                                position: 'absolute',
                                                top: '100%',
                                                left: 0,
                                                zIndex: 10,
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                                overflow: 'hidden',
                                            }}
                                            onBlur={() => setShowCallAcceptanceDropdown(false)}
                                        >
                                            <button
                                                onClick={() => {
                                                    setCallAcceptance('everyone');
                                                    setShowCallAcceptanceDropdown(false);
                                                }}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px',
                                                    background:
                                                        callAcceptance === 'everyone'
                                                            ? 'rgba(102, 126, 234, 0.1)'
                                                            : 'white',
                                                    border: 'none',
                                                    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    textAlign: 'left',
                                                    color: '#333',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        fontWeight: 'bold',
                                                        marginBottom: '3px',
                                                    }}
                                                >
                                                    1: 誰からでもOK
                                                </div>
                                                <div style={{ fontSize: '10px', color: '#666' }}>
                                                    すべてのユーザーからの通話依頼を受け取ります
                                                </div>
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setCallAcceptance('favorites_only');
                                                    setShowCallAcceptanceDropdown(false);
                                                }}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px',
                                                    background:
                                                        callAcceptance === 'favorites_only'
                                                            ? 'rgba(102, 126, 234, 0.1)'
                                                            : 'white',
                                                    border: 'none',
                                                    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    textAlign: 'left',
                                                    color: '#333',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        fontWeight: 'bold',
                                                        marginBottom: '3px',
                                                    }}
                                                >
                                                    2: 自分がお気に入りしている相手からのみ受け取る
                                                </div>
                                                <div style={{ fontSize: '10px', color: '#666' }}>
                                                    お気に入りユーザーからの通話依頼のみ受け取ります
                                                </div>
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setCallAcceptance('none');
                                                    setShowCallAcceptanceDropdown(false);
                                                }}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px',
                                                    background:
                                                        callAcceptance === 'none'
                                                            ? 'rgba(102, 126, 234, 0.1)'
                                                            : 'white',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    textAlign: 'left',
                                                    color: '#333',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        fontWeight: 'bold',
                                                        marginBottom: '3px',
                                                    }}
                                                >
                                                    3: 通話申請を受け入れない
                                                </div>
                                                <div style={{ fontSize: '10px', color: '#666' }}>
                                                    すべての通話依頼を拒否します
                                                </div>
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div
                                    style={{
                                        marginTop: '25px',
                                        paddingTop: '15px',
                                        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                                    }}
                                >
                                    <h3
                                        style={{
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            marginBottom: '10px',
                                            color: '#c62828',
                                        }}
                                    >
                                        ⚠️ アカウント削除
                                    </h3>
                                    <p
                                        style={{
                                            fontSize: '12px',
                                            color: '#555',
                                            marginBottom: '12px',
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        アカウントを削除すると、プロフィールや通話履歴などのデータは復元できません。操作を続行する場合は現在のパスワードを入力してください。
                                    </p>
                                    {deleteError && (
                                        <div
                                            style={{
                                                background: 'rgba(255, 235, 59, 0.2)',
                                                color: '#c62828',
                                                padding: '8px',
                                                borderRadius: '6px',
                                                marginBottom: '10px',
                                                fontSize: '12px',
                                            }}
                                        >
                                            {deleteError}
                                        </div>
                                    )}
                                    {deleteSuccess && (
                                        <div
                                            style={{
                                                background: 'rgba(76, 175, 80, 0.15)',
                                                color: '#2e7d32',
                                                padding: '8px',
                                                borderRadius: '6px',
                                                marginBottom: '10px',
                                                fontSize: '12px',
                                            }}
                                        >
                                            {deleteSuccess}
                                        </div>
                                    )}
                                    {showDeleteConfirm ? (
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '10px',
                                            }}
                                        >
                                            <label
                                                style={{
                                                    fontSize: '12px',
                                                    color: '#555',
                                                }}
                                            >
                                                現在のパスワード
                                            </label>
                                            <input
                                                type='password'
                                                value={deletePassword}
                                                onChange={(e) => setDeletePassword(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px',
                                                    border: '1px solid rgba(0, 0, 0, 0.2)',
                                                    borderRadius: '6px',
                                                    fontSize: '12px',
                                                }}
                                                placeholder='現在のパスワードを入力'
                                                autoComplete='current-password'
                                            />
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    gap: '10px',
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <button
                                                    type='button'
                                                    onClick={handleAccountDeletion}
                                                    disabled={isDeletingAccount}
                                                    style={{
                                                        flex: 1,
                                                        minWidth: '140px',
                                                        padding: '10px',
                                                        background: isDeletingAccount
                                                            ? '#ccc'
                                                            : 'linear-gradient(135deg, #e53935 0%, #d32f2f 100%)',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: isDeletingAccount
                                                            ? 'not-allowed'
                                                            : 'pointer',
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {isDeletingAccount
                                                        ? '削除中...'
                                                        : '完全に削除する'}
                                                </button>
                                                <button
                                                    type='button'
                                                    onClick={() => {
                                                        setShowDeleteConfirm(false);
                                                        setDeletePassword('');
                                                        setDeleteError('');
                                                        setDeleteSuccess('');
                                                    }}
                                                    style={{
                                                        flex: 1,
                                                        minWidth: '100px',
                                                        padding: '10px',
                                                        background: 'rgba(0, 0, 0, 0.05)',
                                                        color: '#333',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    キャンセル
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            type='button'
                                            onClick={() => {
                                                setShowDeleteConfirm(true);
                                                setDeletePassword('');
                                                setDeleteError('');
                                                setDeleteSuccess('');
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                background: 'rgba(229, 57, 53, 0.15)',
                                                color: '#c62828',
                                                border: '1px solid rgba(229, 57, 53, 0.3)',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            アカウントを削除する
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 保存ボタン（スマホのみ固定） */}
                    {isMobile && showSaveButton && (
                        <div
                            style={{
                                position: 'fixed',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                padding: `max(env(safe-area-inset-bottom, 0px), 10px) 15px 10px 15px`,
                                background:
                                    'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 50%, #dcdcdc 100%)',
                                borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                                boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
                                zIndex: 200,
                                boxSizing: 'border-box',
                            }}
                        >
                            <button
                                onClick={handleSave}
                                disabled={saving || !username}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    background:
                                        saving || !username
                                            ? '#ccc'
                                            : 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: saving || !username ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                                }}
                            >
                                {saving ? '保存中...' : '💾 保存する'}
                            </button>
                        </div>
                    )}
                </div>
            </>
        );
    }
);

ProfileSettingScreen.displayName = 'ProfileSettingScreen';

export default ProfileSettingScreen;
